import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Badge,
    Box,
    Button,
    Flex,
    IconButton,
    Switch,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import {
    CellContext,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';
import { useMemo, useRef, useState } from 'react';
import ErrorState from '../../../components/states/ErrorState';
import Loader from '../../../components/Loader';
import { translate } from '../../../i18n';
import { ArticleDto } from '../../../models/article/dto/article.dto';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useDeleteArticle } from '../hooks/useDeleteArticle';
import { useListArticles } from '../hooks/useListArticles';
import CreateArticleForm from './CreateArticleForm';
import { tableProps, tableThProps, tableThFlexProps, tableCellProps, tableHeaderTextProps } from 'theme/constants';

const columnHelper = createColumnHelper<ArticleDto>();

export default function ArticleList() {
    const [includeUnpublished, setIncludeUnpublished] = useState(false);
    const { selectedOrganization } = useSelectedOrganization();
    const { data, isInitialLoading, isError } = useListArticles(selectedOrganization?.id || '', includeUnpublished);
    // `enabled: !!organizationId`. The raw `isLoading` this used to return a
    // <Loader/> on never goes false for a disabled query, so the page was a
    // permanent spinner for any session with no organization selected.
    const readState = classifyRead(isError, isInitialLoading, data);
    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const toast = useToast();

    // Edit modal
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const [editingArticle, setEditingArticle] = useState<ArticleDto | null>(null);

    // Delete modal
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [deletingArticle, setDeletingArticle] = useState<ArticleDto | null>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);

    // Mutations
    const { mutateAsync: deleteArticle, isLoading: isDeleting } = useDeleteArticle();

    const handleEditArticle = (article: ArticleDto) => {
        setEditingArticle(article);
        onEditOpen();
    };

    const handleCloseEdit = () => {
        setEditingArticle(null);
        onEditClose();
    };

    const handleDeleteArticle = (article: ArticleDto) => {
        setDeletingArticle(article);
        onDeleteOpen();
    };

    const handleConfirmDelete = async () => {
        if (!deletingArticle) return;

        try {
            await deleteArticle(deletingArticle.id);
            toast({
                title: translate('article.delete_success') || 'Article deleted successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onDeleteClose();
            setDeletingArticle(null);
        } catch (error: any) {
            toast({
                title: translate('article.delete_error') || 'Error deleting article',
                description: error.message || translate('article.delete_error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const columns = useMemo(() => [
        columnHelper.accessor('title', {
            id: 'title',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('article.title') || 'Title'}
                </Text>
            ),
            cell: (info: CellContext<ArticleDto, string>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('authorName', {
            id: 'author',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('article.author') || 'Author'}
                </Text>
            ),
            cell: (info: CellContext<ArticleDto, string>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('readingTimeMinutes', {
            id: 'readingTime',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('article.reading_time') || 'Reading Time'}
                </Text>
            ),
            cell: (info: CellContext<ArticleDto, number>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue() ? `${info.getValue()} min` : '-'}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('isPopular', {
            id: 'isPopular',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('article.popular') || 'Popular'}
                </Text>
            ),
            cell: (info: CellContext<ArticleDto, boolean>) => (
                <Flex align='center'>
                    <Badge colorScheme={info.getValue() ? 'purple' : 'gray'}>
                        {info.getValue() ? translate('common.yes') || 'Yes' : translate('common.no') || 'No'}
                    </Badge>
                </Flex>
            )
        }),
        columnHelper.accessor('isPublished', {
            id: 'isPublished',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('article.status') || 'Status'}
                </Text>
            ),
            cell: (info: CellContext<ArticleDto, boolean>) => (
                <Flex align='center'>
                    <Badge colorScheme={info.getValue() ? 'green' : 'orange'}>
                        {info.getValue()
                            ? translate('article.published') || 'Published'
                            : translate('article.draft') || 'Draft'}
                    </Badge>
                </Flex>
            )
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('article.created_at') || 'Created At'}
                </Text>
            ),
            cell: (info: CellContext<ArticleDto, string>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {new Date(info.getValue()).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('common.actions') || 'Actions'}
                </Text>
            ),
            cell: (info: CellContext<ArticleDto, unknown>) => (
                <Flex align='center' gap={2}>
                    <IconButton
                        aria-label={translate('article.edit') || 'Edit article'}
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditArticle(info.row.original)}
                    />
                    <IconButton
                        aria-label={translate('article.delete_confirm') || 'Delete article'}
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDeleteArticle(info.row.original)}
                    />
                </Flex>
            )
        }),
    ], [textColor]);

    const table = useReactTable({
        data: data || [],
        columns,
        state: {
            sorting
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (readState === 'never-issued') {
        return (
            <PageCard title={translate('article.articles') || 'Articles'}>
                <ScopePrompt gate='organization' />
            </PageCard>
        );
    }

    if (readState === 'loading') {
        return <Loader isLoading />;
    }

    if (isError) {
        return (
            <PageCard title={translate('article.articles') || 'Articles'}>
                <ErrorState message={translate('article.error_loading') || 'Failed to load articles'} />
            </PageCard>
        );
    }

    return (
        <PageCard
            title={translate('article.articles') || 'Articles'}
            rightHeader={
                <Flex gap={2} align="center">
                    <Switch
                        isChecked={includeUnpublished}
                        onChange={(e) => setIncludeUnpublished(e.target.checked)}
                    />
                    <Text fontSize='sm'>{translate('article.show_unpublished') || 'Show Unpublished'}</Text>
                    <Button
                        colorScheme="brand"
                        onClick={onEditOpen}
                        size="sm"
                    >
                        {translate('article.create') || 'Create Article'}
                    </Button>
                </Flex>
            }
        >
            {data?.length === 0 ? (
                <Flex px='25px' py='40px' justifyContent='center'>
                    <Text color='gray.500'>{translate('article.no_articles') || 'No articles yet. Create your first article!'}</Text>
                </Flex>
            ) : (
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <Th
                                        {...tableThProps}
                                        key={header.id}
                                        borderColor={borderColor}
                                        cursor='pointer'
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <Flex
                                            {...tableThFlexProps}>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            <SortIndicator direction={header.column.getIsSorted()} />
                                        </Flex>
                                    </Th>
                                ))}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map(row => (
                            <Tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <Td
                                        key={cell.id}
                                        {...tableCellProps}
                                        borderColor={borderColor}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Td>
                                ))}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
            )}

            <CreateArticleForm
                article={editingArticle || undefined}
                isOpen={isEditOpen}
                onClose={handleCloseEdit}
            />

            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelButtonRef}
                onClose={onDeleteClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            {translate('article.delete_confirm') || 'Delete Article'}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {translate('article.delete_confirm_message') || 'Are you sure? You can\'t undo this action afterwards.'}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelButtonRef} onClick={onDeleteClose}>
                                {translate('common.cancel') || 'Cancel'}
                            </Button>
                            <Button colorScheme='red' onClick={handleConfirmDelete} ml={3} isLoading={isDeleting}>
                                {translate('common.delete') || 'Delete'}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </PageCard>
    );
}
